import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { ProductDetail } from '@/types';

function formatNaira(numStr: string) {
  const n = Number(numStr);
  if (!Number.isFinite(n)) return numStr;
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
  }).format(n);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('products.manage');

  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ProductDetail>(`/back-office/products/${id}`);
      setDetail(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load product'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleFeatured = async () => {
    if (!detail || !canManage) return;
    const next = !detail.product.is_featured;
    const verb = next ? 'feature' : 'unfeature';
    if (
      !window.confirm(
        next
          ? `Feature "${detail.product.name}" in the marketplace spotlight?`
          : `Remove "${detail.product.name}" from the marketplace spotlight?`
      )
    )
      return;
    setActing(true);
    try {
      await api.post(`/back-office/products/${detail.product.id}/${verb}`);
      await load();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, `Failed to ${verb}`));
    } finally {
      setActing(false);
    }
  };

  const toggleDisabled = async () => {
    if (!detail || !canManage) return;
    const next = !detail.product.disabled;
    const verb = next ? 'disable' : 'enable';
    if (
      !window.confirm(
        `${verb[0].toUpperCase()}${verb.slice(1)} "${detail.product.name}"?`
      )
    )
      return;
    setActing(true);
    try {
      await api.post(`/back-office/products/${detail.product.id}/${verb}`);
      await load();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, `Failed to ${verb}`));
    } finally {
      setActing(false);
    }
  };

  if (loading) return <div className="px-8 py-6 text-slate-500">Loading…</div>;

  if (error || !detail) {
    return (
      <div className="px-8 py-6">
        <Link to="/products" className="text-sm text-brand-600 hover:underline">
          ← Back to products
        </Link>
        <div className="mt-4 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error ?? 'Product not found'}
        </div>
      </div>
    );
  }

  const { product, store, category, owner } = detail;
  const disabled = !!product.disabled;
  const featured = !!product.is_featured;
  const status = product.save_as ?? 'published';
  // The marketplace hero slot only surfaces products a shopper can reach.
  const canBeFeatured = !disabled && status === 'published';

  return (
    <div className="px-8 py-6">
      <Link to="/products" className="text-sm text-brand-600 hover:underline">
        ← Back to products
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            {product.name}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span>ID #{product.id}</span>
            <span>·</span>
            <span>{formatNaira(product.price)}</span>
            <span>·</span>
            <span>{product.quantity} in stock</span>
            <span>·</span>
            <span
              className={clsx(
                'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                status === 'published'
                  ? 'bg-emerald-100 text-emerald-700'
                  : status === 'archived'
                    ? 'bg-slate-200 text-slate-700'
                    : 'bg-amber-100 text-amber-700'
              )}
            >
              {status}
            </span>
            {disabled && (
              <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                disabled
              </span>
            )}
            {featured && (
              <span className="inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                featured
              </span>
            )}
          </div>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={toggleFeatured}
              disabled={acting || (!featured && !canBeFeatured)}
              title={
                !featured && !canBeFeatured
                  ? 'Publish and enable this product before featuring it.'
                  : undefined
              }
              className={clsx(
                'rounded border px-4 py-2 text-sm font-medium disabled:opacity-50',
                featured
                  ? 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'
              )}
            >
              {acting ? '…' : featured ? 'Unfeature' : 'Feature on marketplace'}
            </button>
            <button
              type="button"
              onClick={toggleDisabled}
              disabled={acting}
              className={clsx(
                'rounded border px-4 py-2 text-sm font-medium disabled:opacity-50',
                disabled
                  ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                  : 'border-red-300 text-red-700 hover:bg-red-50'
              )}
            >
              {acting ? '…' : disabled ? 'Enable product' : 'Disable product'}
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section title="Description">
            <p className="whitespace-pre-line text-sm text-slate-700">
              {product.description || (
                <span className="text-slate-400">No description provided.</span>
              )}
            </p>
          </Section>

          <Section title="Catalog">
            <DetailRow label="Category" value={category?.name ?? '—'} />
            <DetailRow label="Status" value={status} />
            <DetailRow
              label="Visibility"
              value={disabled ? 'Disabled (hidden)' : 'Visible'}
            />
            <DetailRow
              label="Marketplace spotlight"
              value={featured ? 'Featured' : 'Not featured'}
            />
            <DetailRow label="Created" value={formatDate(product.created_at)} />
            <DetailRow label="Updated" value={formatDate(product.updated_at)} />
          </Section>
        </div>

        <div className="space-y-6 lg:col-span-1">
          <Section title="Store">
            {store ? (
              <>
                <DetailRow label="Store" value={store.store_name ?? '—'} />
                <DetailRow label="Email" value={store.store_email ?? '—'} />
                <DetailRow label="Phone" value={store.store_phone ?? '—'} />
              </>
            ) : (
              <div className="text-sm text-slate-500">
                No store linked to this product.
              </div>
            )}
          </Section>

          <Section title="Owner">
            {owner ? (
              <>
                <DetailRow
                  label="Name"
                  value={
                    <Link
                      to={`/users/${owner.id}`}
                      className="text-brand-600 hover:underline"
                    >
                      {owner.first_name} {owner.last_name}
                    </Link>
                  }
                />
                <DetailRow label="Email" value={owner.email} />
              </>
            ) : (
              <div className="text-sm text-slate-500">No owner found.</div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded border border-slate-200 bg-white p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right text-slate-800">{value}</span>
    </div>
  );
}
